import {
	find,
	forEach,
	get,
	includes,
	isNumber,
	map,
	max,
	min,
	reduce,
	set,
} from 'lodash-es';
import {
	CropStateType,
	elementBeanListType,
	floTrackType,
	pauseTimeType,
	publishElementsDataType,
	SwiftPlayImageType,
} from '../Pages/Flo/Flo.types';
import linkifyStr from 'linkify-string';
import {
	MAX_MOBILE_VIEW_HEIGHT_IN_LANDSCAPE,
	MAX_MOBILE_VIEW_WIDTH,
	MAX_TABLET_VIEW_WIDTH,
} from './Common.params';
import store from '../store';
import qs, { ParsedQs } from 'qs';
import { scaleLinear } from 'd3-scale';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const BGM_MIN_VOLUME_MULTIPLIER = 0.1;
export const floikColour = '#655BE1';

interface ResourceType {
	extension: string;
	status: string;
	url: string;
	id: string;
	resourceType: string;
	distributionUrl: string;
	resourceObject: string;
	resourceProperties: {
		recordTimeInSeconds: number;
		hasAudio: boolean;
		time: number;
	};
	hasThumbnails: boolean;
	thumbnailsPath: string;
	cookieDefinition: unknown;
}

interface GroupResourceType {
	type: string;
	resources: ResourceType[];
}

interface ResourceGroupTrackResponse {
	id: string;
	floId: string;
	resources?: ResourceType[];
	resourceGroupDetailList?: GroupResourceType[];

	type: string;
}

export interface Subtitle {
	startTime: number;
	endTime: number;
	text: string;
}

function parseTime(timeString: string): number {
	const [hours, minutes, seconds] = timeString.split(':');
	const [secondsPart, milliseconds] = seconds.split('.');
	return (
		parseInt(hours) * 3600 +
		parseInt(minutes) * 60 +
		parseInt(secondsPart) +
		parseFloat('0.' + milliseconds)
	);
}

export interface TranscriptSegment {
	id: string;
	startTime: string;
	startTimeInMillis: number;
	endTime: string;
	endTimeInMillis: number;
	text: string;
	languageCode: string;
	wpm: number;
	enabled: boolean;
	conflict: boolean;
	modifiedAt: number;
}

export interface Segment {
	enabled: boolean;
	text: string;
	startTimeInMillis: number;
	endTimeInMillis: number;
	[key: string]: any;
}

interface SubtitleOutput {
	enabled: boolean;
	text: string;
	startTimeInMillis: number;
	endTimeInMillis: number;
	[key: string]: any;
}

export function convertTranscriptToSubtitles(segments: Segment[]): Subtitle[] {
	const subtitles: Subtitle[] = [];

	for (const segment of segments) {
		if (!segment.enabled || !segment.text) continue;

		const startSec = segment.startTimeInMillis / 1000;
		const endSec = segment.endTimeInMillis / 1000;
		const totalDuration = endSec - startSec;

		// Split text into sentences by sentence-ending punctuation
		const sentences = segment.text
			.split(/(?<=[.!?।])\s+/)
			.filter((s) => s.trim().length > 0);

		if (sentences.length === 0) continue;

		// Calculate total word count for proportional time distribution
		const wordCounts = sentences.map(
			(s) => s.split(/\s+/).filter((w) => w.length > 0).length
		);
		const totalWords = wordCounts.reduce((sum, count) => sum + count, 0);

		if (totalWords === 0) continue;

		let currentStart = startSec;

		for (let i = 0; i < sentences.length; i++) {
			const proportion = wordCounts[i] / totalWords;
			const duration = totalDuration * proportion;
			const currentEnd = Math.min(currentStart + duration, endSec);

			subtitles.push({
				startTime: parseFloat(currentStart.toFixed(3)),
				endTime: parseFloat(currentEnd.toFixed(3)),
				text: sentences[i].trim(),
			});

			currentStart = currentEnd;
		}
	}

	return subtitles;
}

export function transcriptSegmentsToVttBlobUrl(segments: SubtitleOutput[]): string {
	const formatVttTime = (ms: number): string => {
		const totalSeconds = ms / 1000;
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;
		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
			2,
			'0'
		)}:${seconds.toFixed(3).padStart(6, '0')}`;
	};

	let vtt = 'WEBVTT\n\n';
	segments.forEach((segment, index) => {
		if (!segment.enabled || !segment.text) return;
		const text = segment.text.trim();
		if (!text) return;
		vtt += `${index + 1}\n`;
		vtt += `${formatVttTime(segment.startTimeInMillis)} --> ${formatVttTime(
			segment.endTimeInMillis
		)}\n`;
		vtt += `${text}\n\n`;
	});

	const blob = new Blob([vtt], { type: 'text/vtt' });
	return URL.createObjectURL(blob);
}

export function parseVttFile(vttText: string) {
	const lines = vttText.trim().split(/\r?\n/);

	if (lines[0].toUpperCase() === 'WEBVTT') {
		lines.shift();
	}

	const cues: Subtitle[] = [];
	let cue: Subtitle | null = null;
	let cueText = '';

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i].trim();

		if (!cue && line.includes('-->')) {
			cue = {} as Subtitle;
			const [startTime, endTime] = line.split('-->');
			cue.startTime = parseTime(startTime);
			cue.endTime = parseTime(endTime);
			cueText = '';
		} else if (cue && line === '' && cueText.trim()) {
			cue.text = cueText.trim();
			// Only push valid cues: non-empty text and valid time range
			if (cue.text && cue.startTime < cue.endTime) {
				cues.push(cue);
			}
			cue = null;
		} else if (cue) {
			cueText += line + ' ';
		}
		if (cue && cueText && lines.length - 1 === i) {
			cue.text = cueText.trim();
			// Only push valid cues: non-empty text and valid time range
			if (cue.text && cue.startTime < cue.endTime) {
				cues.push(cue);
			}
		}
	}
	return cues;
}

export type mergedType = {} & floTrackType & {
		images: Array<SwiftPlayImageType>;
		floImageElements: {
			[key: string]: string;
		};
	};

export function transformResourceGroupToUISourceList(
	data: ResourceGroupTrackResponse[]
): mergedType[] {
	let hasAudioTrack = false;

	// @ts-ignore
	return map(data, (item) => {
		const values = reduce(
			item.resources,
			(acc, resource: ResourceType) => {
				if (includes(resource.extension, 'm3u8')) {
					acc.hlsURL = resource.url;
					if (get(resource, 'resourceProperties.recordTimeInSeconds')) {
						acc.duration = get(resource, 'resourceProperties.recordTimeInSeconds');
					}
					if (get(resource, 'resourceProperties.height')) {
						acc.height = get(resource, 'resourceProperties.height', 0);
					}
					if (get(resource, 'resourceProperties.width')) {
						acc.width = get(resource, 'resourceProperties.width', 0);
					}
				} else if (includes(resource.extension, 'webm')) {
					acc.url = resource.url;
				}
				if (resource.hasThumbnails) {
					acc.hasThumbnails = resource.hasThumbnails;
					acc.thumbnailsPath = resource.thumbnailsPath;
				}
				if (
					includes(resource.extension, 'png') ||
					includes(resource.extension, 'jpg') ||
					includes(resource.extension, 'jpeg') ||
					includes(resource.extension, 'gif') ||
					includes(resource.extension, 'svg') ||
					includes(resource.extension, 'webp')
				) {
					const id =
						get(resource, 'resourceProperties.elementId', '') ||
						get(resource, 'resourceProperties.imageId', '');

					if (id) {
						// @ts-ignore
						acc.floImageElements[id] = get(resource, 'url');
					}
				}
				if (includes(resource.resourceType, 'HOTSPOT_IMAGE_CAPTURE_PUBLISHED')) {
					// @ts-ignore
					acc.images.push({
						id: resource.id,
						url: resource.url,
						resourceType: resource.resourceType,
						time: Math.round(get(resource, 'resourceProperties.time') * 100) / 100,
					} as SwiftPlayImageType);
				}
				if (includes(resource.resourceType, 'THUMBNAIL')) {
					acc.hasThumbnails = true;
					acc.thumbnailsPath = resource.url;
				}

				if (includes(resource.resourceType, 'TRANSCRIBE')) {
					acc.subtitleUrl = resource.url;
				}

				if (resource.cookieDefinition) {
					// @ts-ignore
					acc.cookieDefinition = resource.cookieDefinition;
				}
				if (
					includes(
						['PUBLISHED_BACKGROUND_MUSIC', 'PUBLISHED_STATIC_BACKGROUND_MUSIC'],
						resource.resourceType
					)
				) {
					acc.backgroundMusicResource = {
						...resource.resourceProperties,
						url: resource.url,
						textPauses: get(resource, 'resourceProperties.speechSegments', []),
					};
				}
				if (includes(resource.resourceType, 'PUBLISH_ELEMENT_VOICE_OVER')) {
					const key = `${get(resource, 'resourceProperties.scriptId', '')}_${get(
						resource,
						'resourceProperties.voiceId',
						''
					)}`;
					acc.elementVoiceResources = {
						...acc.elementVoiceResources,
						[key]: {
							...get(resource, 'resourceProperties', {}),
							url: get(resource, 'url'),
						},
					};
				}

				acc.distributionUrl = get(resource, 'distributionUrl');
				acc.resourceObject = get(resource, 'resourceObject');
				if (get(resource, 'resourceProperties.hasAudio')) {
					acc.hasAudio = get(resource, 'resourceProperties.hasAudio');
				}

				return acc;
			},
			{
				images: [],
				floImageElements: {},
				distributionUrl: '',
				resourceObject: '',
				hlsURL: '',
				url: '',
				subtitleUrl: '',
				duration: 0,
				height: 0,
				width: 0,
				hasThumbnails: false,
				hasAudio: false,
				thumbnailsPath: '',
				cookieDefinition: undefined,
				elementVoiceResources: {},
				backgroundMusicResource: {},
			}
		);
		return {
			...values,
			id: item.id,
			type: item.type,
		};
	});
}

export const linkify = (t: string) => {
	return linkifyStr(t, {
		target: '_blank',
		validate: true,
	});
};

export const convertNodesToString = (nodes: ChildNode[]) => {
	if (!nodes) return '';
	//@ts-ignore
	return nodes?.map((node) => node.outerHTML || node.nodeValue).join('');
};

export const convertStringToNodes = (s: string) => {
	const doc = new DOMParser().parseFromString(s || '', 'text/html');
	return Array.from(doc.body.childNodes);
};

export const sanitizeHtml = (str: string) => {
	if (!str) return '';
	let s = (str || '').replace(/<br>/g, '').trim();
	if (s === '<p></p>' || s === '<p><br></p>' || s === '<p><br/></p>') {
		return '';
	}

	const outArr: ChildNode[] = [];
	const nodeArr = convertStringToNodes(s);
	nodeArr.forEach((node) => {
		if (node.hasChildNodes() && node.nodeName !== 'A') {
			const arr = Array.from(node.childNodes);
			// @ts-ignore
			const sanitizedArr = arr.map(
				(
					child // @ts-ignore
				) => sanitizeHtml(child?.outerHTML || child.nodeValue)
			);
			return outArr.push(...convertStringToNodes(sanitizedArr.join('')));
		}
		if (node.nodeName === '#text') {
			const str = convertNodesToString([node]);
			const link = linkify(str);
			return outArr.push(...convertStringToNodes(link));
		}
		if (node.nodeName === 'A') {
			// @ts-ignore
			node.setAttribute('target', '_blank');
		}
		return outArr.push(node);
	});
	return convertNodesToString(outArr);
};

const modifyAnchorTags = (nodes: ChildNode[]): ChildNode[] => {
	const modifiedNodes: ChildNode[] = [];
	for (const node of nodes) {
		if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'A') {
			// @ts-ignore
			node.setAttribute('target', '_blank');
			modifiedNodes.push(node);
		} else if (node.hasChildNodes()) {
			const modifiedChildren = modifyAnchorTags(Array.from(node.childNodes));
			const newNode = node.cloneNode(false);
			for (const child of modifiedChildren) {
				newNode.appendChild(child);
			}
			// @ts-ignore
			modifiedNodes.push(newNode);
		} else {
			modifiedNodes.push(node);
		}
	}
	return modifiedNodes;
};

export const sanitizeHtmlLinks = (str: string) => {
	if (!str) return '';
	let s = (str || '').trim();
	if (s === '<p></p>' || s === '<p><br></p>' || s === '<p><br/></p>') {
		return '';
	}

	const doc = new DOMParser().parseFromString(s || '', 'text/html');
	// @ts-ignore
	const modifiedNodes = modifyAnchorTags(doc.body.childNodes);
	return convertNodesToString(modifiedNodes);
};

export const getWindowDimensions = (floIndex?: number) => {
	const elements = document.querySelectorAll(`floik-flo`);
	console.log('getWindowDimensions[log]', { elements, floIndex });
	if (elements.length > 0 && isNumber(floIndex) && floIndex >= 0) {
		return {
			width: get(elements, `[${floIndex}].clientWidth`),
			height: get(elements, `[${floIndex}].clientHeight`),
		};
	}
	return {
		width: window.innerWidth,
		height: window.innerHeight,
	};
};

export const getIsMobileView = () =>
	(window.innerWidth <= MAX_MOBILE_VIEW_WIDTH &&
		window.innerWidth >= 330 &&
		window.innerHeight >= 300 &&
		window.innerHeight > window.innerWidth) ||
	(window.innerHeight <= MAX_MOBILE_VIEW_HEIGHT_IN_LANDSCAPE &&
		window.innerHeight < window.innerWidth &&
		window.innerWidth >= 600 &&
		window.innerHeight >= 200 &&
		!!window.screen?.orientation?.angle &&
		window.screen?.orientation?.angle !== 0);

export const isMicroApp = () => {
	return document.querySelectorAll('[data-micro-view="true"]').length > 0;
};

export const ignoreSecurityCheck = () => {
	const isOldFlo =
		!!document.getElementById('FLOIK_JSON') ||
		document.querySelectorAll('[data-f-id*=_]').length > 0;
	const whiteListedDomains =
		(process.env.REACT_APP_ENVIRONMENT || '') === 'production'
			? ['www.realmofdev.com', 'www.floik.com']
			: ['www.realmofdev.com', 'localhost'];
	const isWhiteListed = includes(whiteListedDomains, location.hostname);
	return isMicroApp() || isOldFlo || isWhiteListed;
};

export const getIsTabView = (floIndex?: number) =>
	get(getWindowDimensions(floIndex), 'width', 0) <= MAX_TABLET_VIEW_WIDTH;

export const isTouchDevice = () =>
	'ontouchstart' in window || navigator.maxTouchPoints > 0;

export const getIsEmbed = (floIndex?: number) => {
	const elements = document.querySelectorAll(`floik-flo`);
	if (elements.length > 0 && isNumber(floIndex) && floIndex >= 0) {
		return elements[floIndex]?.getAttribute('is-embed') === 'true';
	}
	return includes(location.pathname, '/embed');
};

export const isSwiftPlay = (floIndex?: number) => {
	const elements = document.querySelectorAll(`floik-flo`);
	if (elements.length > 0 && isNumber(floIndex) && floIndex >= 0) {
		return elements[floIndex]?.getAttribute('swift-play') === 'true';
	}
	const queryParam = qs.parse(location.search.replace('?', ''));
	return get(queryParam, 'skip-video', 'false') === 'true';
};

const getAppLoadingMode = (): 'script' | 'embed' | 'share-link' => {
	const floikElements = document.querySelectorAll('floik-flo');
	const hasScriptEmbed = Array.from(floikElements).some(
		(el) => el.getAttribute('is-embed') === 'true'
	);
	const hasEmbedContainer =
		document.querySelector('[id^="floik-iframe-container-"]') !== null;
	const hasEmbedIframe = Array.from(document.querySelectorAll('iframe')).some((iframe) =>
		iframe.src.includes('/embed/')
	);
	const hasEmbedInUrl = hasEmbedContainer && hasEmbedIframe;
	const hasEmbedInUrlInLocation = location.pathname.includes('/embed/');
	if (hasEmbedInUrl || hasEmbedInUrlInLocation) {
		return 'embed';
	} else if (hasScriptEmbed) {
		return 'script';
	} else {
		return 'share-link';
	}
};

export const getFloAttributeOrQueryParamValue = ({
	floId,
	key,
	floIndex,
	resourceShareInfo,
	queryParamKey,
	isBooleanOptions,
}: {
	floId?: string;
	key: string;
	floIndex?: number;
	isBooleanOptions?: boolean;
	resourceShareInfo?: {
		publicResource: boolean;
		scriptCustomisationConfig: any;
		shareLinkCustomisationConfig: any;
		embedLinkCustomisationConfig: any;
		ssoProviderConfigurations: any;
	};
	queryParamKey?: string;
}) => {
	const queryParam = qs.parse(location.search.replace('?', ''));
	const value = get(queryParam, queryParamKey || key, null);
	if (value !== null) {
		return value === 'true' ? true : value === 'false' ? false : value;
	}
	const loadingMode = getAppLoadingMode();

	if (!isMicroApp() && resourceShareInfo) {
		let keyMap = {
			view: 'layout',
			'swift-play': 'skip-video',
		};
		const categoryMap = {
			script: 'scriptCustomisationConfig',
			embed: 'embedLinkCustomisationConfig',
			'share-link': 'shareLinkCustomisationConfig',
		};

		if (loadingMode === 'script') {
			const elements = document.querySelectorAll(`floik-flo`);
			if (elements.length > 0 && isNumber(floIndex) && floIndex >= 0) {
				const attrValue = elements[floIndex]?.getAttribute(key);
				if (attrValue !== null) {
					return attrValue === 'true' ? true : attrValue === 'false' ? false : attrValue;
				}
			}
		} else {
			const categoryConfig = categoryMap[loadingMode];
			const configurations = get(resourceShareInfo, categoryConfig, []) as Array<{
				configurationId?: string;
				configurationLabel?: string;
				configurationType?: string;
				selectedOption?: any;
				customValue?: string;
				booleanValue?: boolean;
			}>;
			const matchKey = keyMap?.[key as keyof typeof keyMap] || key;

			const matchingConfig = configurations.find(
				(config) =>
					config.configurationType === matchKey ||
					(config.configurationType === 'booleanOptions' &&
						isBooleanOptions &&
						config.selectedOption.key === matchKey)
			);
			if (matchingConfig && isBooleanOptions) {
				const configKey = matchingConfig?.selectedOption?.key;
				if (configKey === matchKey) {
					return matchingConfig?.booleanValue || false;
				}
			}
			if (matchingConfig) {
				const configValue = matchingConfig?.selectedOption?.key;
				if (configValue === 'custom' || configValue === 'brand') {
					let colorWithoutHash = matchingConfig?.customValue?.slice(1);
					return `${configValue}_${colorWithoutHash}`;
				}
				if (configValue !== null && configValue !== undefined) {
					return configValue === 'true' || configValue === true
						? true
						: configValue === 'false' || configValue === false
						? false
						: configValue;
				}
			}
		}
	}

	return null;
};

export const removeFloikLoaderInMicroApp = (floIndex: number) => {
	const element = document.querySelectorAll(`floik-flo`)[floIndex];
	if (element) {
		const loader = element.querySelector('floik-loader');
		if (loader) {
			element.removeChild(loader);
		}
	}
};

export const generateFloLink = (url: string, internalLink: boolean, floIndex: number) => {
	if (!internalLink || !url) {
		return url;
	}
	const queryObj = qs.parse(location.search.replace('?', ''));
	const isEmbed = getIsEmbed(floIndex);
	const isUrlEmbed = includes(url, '/embed');
	let addHideHeader = isEmbed && !isUrlEmbed;
	const obj: { [key: string]: boolean } = {};
	if (addHideHeader) {
		obj['hide-header'] = true;
	}
	const queryString = internalLink
		? qs.stringify({
				...queryObj,
				...obj,
		  })
		: '';
	return `${url}${queryString ? '?' : ''}${queryString}`;
};

export const getBlurElementPosition = ({
	isCropped,
	appliedCrop,
	element,
	width,
	height,
	offsetHeight,
	offsetWidth,
}: {
	isCropped?: boolean;
	appliedCrop?: CropStateType;
	element: unknown;
	width: number;
	height: number;
	offsetHeight: number;
	offsetWidth: number;
}) => {
	if (isCropped) {
		const croppedHeight = Math.abs(
			get(appliedCrop, 'values.y2', 0) - get(appliedCrop, 'values.y', 0)
		);
		const croppedWidth = Math.abs(
			get(appliedCrop, 'values.x2', 0) - get(appliedCrop, 'values.x', 0)
		);
		const croppedScaleW = scaleLinear(
			[0, get(appliedCrop, 'values.windowSize.width', 0)],
			[0, get(element, 'canvasRenderConfig.windowCoordinates.width', 0)]
		);
		const croppedScaleH = scaleLinear(
			[0, get(appliedCrop, 'values.windowSize.height', 0)],
			[0, get(element, 'canvasRenderConfig.windowCoordinates.height', 0)]
		);
		const scaleX = scaleLinear(
			[
				croppedScaleW(get(appliedCrop, 'values.x', 0)),
				croppedScaleW(get(appliedCrop, 'values.x', 0) + croppedWidth),
			],
			[0, width]
		);

		const scaleY = scaleLinear(
			[
				croppedScaleH(get(appliedCrop, 'values.y', 0)),
				croppedScaleH(get(appliedCrop, 'values.y', 0) + croppedHeight),
			],
			[0, height]
		);
		const scaleXorigin = scaleLinear([0, croppedWidth], [0, width]);

		const scaleYorigin = scaleLinear([0, croppedHeight], [0, height]);
		const pointerX = scaleX(get(element, 'canvasRenderConfig.canvasCoordinates.x', 0));

		const pointerY = scaleY(get(element, 'canvasRenderConfig.canvasCoordinates.y', 0));

		return {
			left: pointerX,
			top: pointerY,
			width: scaleXorigin(get(element, 'canvasRenderConfig.canvasCoordinates.width', 0)),
			height: scaleYorigin(
				get(element, 'canvasRenderConfig.canvasCoordinates.height', 0)
			),
		};
	}

	const yScale = scaleLinear(
		[0, get(element, 'canvasRenderConfig.windowCoordinates.height', 0)],
		[0, height - (offsetHeight || 0) * 2]
	);

	const xScale = scaleLinear(
		[0, get(element, 'canvasRenderConfig.windowCoordinates.width', 0)],
		[0, width - (offsetWidth || 0) * 2]
	);
	return {
		left: xScale(get(element, 'canvasRenderConfig.canvasCoordinates.x', 0)),
		top: yScale(get(element, 'canvasRenderConfig.canvasCoordinates.y', 0)),
		width: xScale(get(element, 'canvasRenderConfig.canvasCoordinates.width', 0)),
		height: yScale(get(element, 'canvasRenderConfig.canvasCoordinates.height', 0)),
	};
};

export const getFloAuthToken = (floId: string) =>
	localStorage.getItem(`floik-${floId}-token`);

export const useElementVoicePlayer = (
	currentElement: publishElementsDataType | undefined | null,
	elementVoiceResources:
		| { [key: string]: { voiceId: string; scriptId: string } }
		| undefined,
	floElements: {
		[key: string]: publishElementsDataType[];
	},
	isMuted: boolean,
	onVolumeChange: Function,
	hasNotPlayed?: boolean,
	displaySubtitles?: boolean,
	transcribeSegments?: any[],
	selectedLanguageIndex?: number | null,
	languageAudioRef?: React.MutableRefObject<{
		pause: () => void;
		continueCurrentSegment: (count?: number) => void;
		hasPlaylist: boolean;
	}>,
	allActiveElements?: {
		cta?: publishElementsDataType | null;
		hotspots?: publishElementsDataType | null;
		image?: publishElementsDataType | null;
		text?: publishElementsDataType | publishElementsDataType[] | null;
		recording?: publishElementsDataType | null;
	},
	ignoreLanguageAudioOverlap?: boolean
): {
	onElementVoiceMuteClick(): void;
	showElementVoiceIcon: boolean;
	subTitle: string;
	isElementVoicePlaying: boolean;
	onReplayClick(): void;
	elementVoiceTooltipTitle: string;
	voiceOverDuration: number;
} => {
	const [curElementVoiceAudio, setCurElementVoiceAudio] =
		useState<HTMLAudioElement | null>(null);
	const [hasAudioPlayed, setAudioPlayed] = useState(false);
	const [curVoiceUrl, setCurVoiceUrl] = useState('');
	const [subtitleIndex, setSubtitleIndex] = useState<number>();
	const [isElementVoicePlaying, setElementVoicePlaying] = useState(false);
	const resumeElementVoiceAfterVisibilityRef = useRef(false);
	const [playFakeTimeout, setPlayFakeTimeout] = useState<ReturnType<typeof setTimeout>>();
	const [playFakeTimeoutStartTime, setPlayFakeTimeoutStartTime] = useState(0);

	const { voiceProperties, imageElement } = useMemo(
		() =>
			!get(currentElement, 'linkedRecordingId')
				? getElementVoiceResources(currentElement, elementVoiceResources, floElements)
				: {
						voiceProperties: {},
						imageElement: undefined,
				  },
		[currentElement, elementVoiceResources]
	);

	const subtitlesList = get(voiceProperties, 'chunks');

	const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>();

	const showSubtitle = useCallback(
		(index?: number) => {
			const newIndex = !isNumber(index) ? 0 : index;
			if (newIndex < get(subtitlesList, 'length', 0) && displaySubtitles) {
				setSubtitleIndex(newIndex);
				const currentStart = get(subtitlesList, `[${newIndex}].startTime`, 0);
				const currentEnd = get(subtitlesList, `[${newIndex}].endTime`, 0);
				const duration = currentEnd - currentStart;
				// Schedule the next subtitle based on the gap between this
				// subtitle's start and the next subtitle's start so that
				// gaps are respected. Falls back to the segment duration
				// when there is no next subtitle.
				const nextStart = get(subtitlesList, `[${newIndex + 1}].startTime`, currentEnd);
				const delay = Math.max(nextStart - currentStart, duration);
				timeoutsRef.current = timeoutsRef.current || [];
				timeoutsRef.current.push(setTimeout(() => showSubtitle(newIndex + 1), delay));
			}
		},
		[setSubtitleIndex, subtitlesList]
	);

	const resetState = useCallback(() => {
		setCurElementVoiceAudio(null);
		setCurVoiceUrl('');
		setSubtitleIndex(-1);
		forEach(timeoutsRef.current || [], (timeout) => {
			clearTimeout(timeout);
		});
	}, [setCurElementVoiceAudio, setCurVoiceUrl, setSubtitleIndex, timeoutsRef]);

	useEffect(() => {
		if (curElementVoiceAudio) {
			curElementVoiceAudio.muted = isMuted;
		}
	}, [isMuted, curElementVoiceAudio]);

	const onElementVoiceMuteClick = useCallback(() => {
		onVolumeChange({ isMuted: !isMuted, volume: isMuted ? 1 : 0 });
		if (curElementVoiceAudio) {
			curElementVoiceAudio.muted = !isMuted;
			if (playFakeTimeout) {
				const timeElapsed = new Date().getTime() - playFakeTimeoutStartTime;
				const duration = curElementVoiceAudio.duration * 1000;
				if (duration > timeElapsed) {
					curElementVoiceAudio.currentTime = timeElapsed / 1000;
					curElementVoiceAudio.play();
				}
				clearTimeout(playFakeTimeout);
				setPlayFakeTimeout(undefined);
			}
		}
	}, [
		curElementVoiceAudio,
		hasAudioPlayed,
		showSubtitle,
		playFakeTimeout,
		setPlayFakeTimeout,
		playFakeTimeoutStartTime,
		onVolumeChange,
		isMuted,
	]);

	const voiceUrl = get(voiceProperties, 'url', '');

	useEffect(() => {
		if (curElementVoiceAudio) {
			if (typeof document !== 'undefined' && document.hidden) {
				resumeElementVoiceAfterVisibilityRef.current = true;
				return;
			}
			curElementVoiceAudio
				.play()
				.then(() => {
					setAudioPlayed(true);
				})
				.catch((reason) => {
					if (reason?.name === 'NotAllowedError' && curElementVoiceAudio.muted) {
						curElementVoiceAudio.onloadedmetadata = () => {
							if (isNumber(curElementVoiceAudio.duration)) {
								setElementVoicePlaying(true);
								showSubtitle();
								setPlayFakeTimeout(
									setTimeout(() => {
										setElementVoicePlaying(false);
										setAudioPlayed(true);
										setPlayFakeTimeout(undefined);
									}, curElementVoiceAudio.duration * 1000)
								);
								setPlayFakeTimeoutStartTime(new Date().getTime());
							}
						};
					}
				});
		}

		return () => {
			if (curElementVoiceAudio) {
				curElementVoiceAudio.pause();
			}
		};
	}, [
		curElementVoiceAudio,
		setAudioPlayed,
		setPlayFakeTimeout,
		setElementVoicePlaying,
		showSubtitle,
		setPlayFakeTimeoutStartTime,
	]);

	useEffect(() => {
		if (!curElementVoiceAudio || typeof document === 'undefined') return;

		const handleVisibilityChange = () => {
			if (document.hidden) {
				if (!curElementVoiceAudio.paused) {
					resumeElementVoiceAfterVisibilityRef.current = true;
					curElementVoiceAudio.pause();
				}
				return;
			}
			if (resumeElementVoiceAfterVisibilityRef.current) {
				resumeElementVoiceAfterVisibilityRef.current = false;
				curElementVoiceAudio.play().catch(() => setElementVoicePlaying(false));
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		handleVisibilityChange();
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			resumeElementVoiceAfterVisibilityRef.current = false;
			curElementVoiceAudio.pause();
		};
	}, [curElementVoiceAudio]);

	useEffect(() => {
		let newVoiceUrl = voiceUrl;
		if (
			!(
				(get(currentElement, 'time') === 0 &&
					get(currentElement, 'editorElementName') === 'cta') ||
				!hasNotPlayed
			)
		) {
			return;
		}

		// Check if this element should play based on priority when multiple elements with voice-over exist
		// Priority order: CTA > Hotspot > Text > Image > Recording
		if (allActiveElements && voiceUrl) {
			const currentElementType = get(currentElement, 'editorElementName');
			const currentElementId = get(currentElement, 'id');

			// Skip this element's voice-over if any higher-priority element active at
			// the same time also has voice-over enabled. Priority order:
			// CTA > Hotspot > Text > Image > Recording — only types listed before the
			// current element are checked, so each element only yields to higher ranks.
			const priorityOrder = ['cta', 'hotspots', 'text', 'image', 'recording'];
			const currentIndex = priorityOrder.indexOf(currentElementType as string);
			const hasHigherPriorityElement =
				currentIndex > 0 &&
				priorityOrder.slice(0, currentIndex).some((type) => {
					const el = allActiveElements[type as keyof typeof allActiveElements];
					if (!el) return false;
					// text can be a single element or an array of active text elements
					if (Array.isArray(el)) {
						return el.some(
							(e) => get(e, 'id') !== currentElementId && get(e, 'enableVoice')
						);
					}
					return get(el, 'id') !== currentElementId && get(el, 'enableVoice');
				});

			if (hasHigherPriorityElement) {
				console.log('ELEMENT VOICE - Skipping due to higher priority element');
				if (curElementVoiceAudio) {
					resetState();
				}
				return;
			}
		}

		// Skip element voice-over if there's an active language audio segment at this time
		// Exception: For nextButton and CTA elements, play element voice after language audio if <2s remain
		// Exception #2: JsonVideoPlayer callers can opt to always play element voice-over
		if (
			!ignoreLanguageAudioOverlap &&
			transcribeSegments &&
			transcribeSegments.length > 0 &&
			selectedLanguageIndex !== null &&
			selectedLanguageIndex !== undefined
		) {
			const elementTimeMs = get(currentElement, 'time', 0) * 1000;
			const activeSegment = transcribeSegments.find((segment: any) => {
				const startTimeMs = get(segment, 'startTimeInMillis', 0);
				const endTimeMs = get(segment, 'endTimeInMillis', 0);
				return elementTimeMs >= startTimeMs && elementTimeMs < endTimeMs;
			});

			if (activeSegment) {
				const hasNextButton = get(currentElement, 'behaviourType') === 'nextButton';
				const isCta = get(currentElement, 'editorElementName') === 'cta';
				const shouldPlayElementVoice = hasNextButton || isCta;

				console.log('ELEMENT VOICE CHECK:', {
					hasNextButton,
					isCta,
					shouldPlayElementVoice,
					behaviourType: get(currentElement, 'behaviourType'),
					editorElementName: get(currentElement, 'editorElementName'),
					elementTime: get(currentElement, 'time'),
					activeSegment: {
						start: get(activeSegment, 'startTimeInMillis'),
						end: get(activeSegment, 'endTimeInMillis'),
					},
				});

				if (shouldPlayElementVoice) {
					// For nextButton and CTA elements, check remaining time in language audio segment
					const endTimeMs = get(activeSegment, 'endTimeInMillis', 0);
					const timeRemainingMs = endTimeMs - elementTimeMs;
					console.log('INTERACTIVE ELEMENT - Time remaining:', timeRemainingMs, 'ms');

					if (timeRemainingMs > 2000) {
						// If more than 2 seconds remain, pause language audio immediately
						console.log('INTERACTIVE ELEMENT - Pausing language audio (>2s remaining)');
						if (languageAudioRef?.current) {
							languageAudioRef.current.pause();
						}
						// Fall through to play element voice
					} else {
						// If 2 seconds or less remain, let language audio finish naturally
						console.log(
							'INTERACTIVE ELEMENT - Letting language audio finish (≤2s remaining)'
						);
						if (languageAudioRef?.current) {
							languageAudioRef.current.continueCurrentSegment();
						}
						// Fall through to play element voice
					}
				} else {
					// For other elements, skip element voice as before
					if (curElementVoiceAudio) {
						resetState();
					}
					return;
				}
			}
		}

		console.log('ELEMENT VOICE CHECK:', currentElement);

		if (
			(get(currentElement, 'enableVoice') ||
				(get(currentElement, 'linkedImageId') && get(imageElement, 'enableVoice'))) &&
			newVoiceUrl !== curVoiceUrl
		) {
			if (newVoiceUrl) {
				const audio = new Audio(newVoiceUrl);
				audio.muted = isMuted;
				audio.onended = () => {
					setElementVoicePlaying(false);
					setAudioPlayed(true);
				};
				audio.onplay = () => {
					setElementVoicePlaying(true);
				};
				showSubtitle();
				setCurElementVoiceAudio(audio);
				setCurVoiceUrl(newVoiceUrl);
				setAudioPlayed(false);
			} else {
				resetState();
			}
		} else if (
			!get(currentElement, 'enableVoice') &&
			curElementVoiceAudio &&
			!(get(currentElement, 'linkedImageId') && get(imageElement, 'enableVoice'))
		) {
			resetState();
		}
	}, [
		currentElement,
		setCurElementVoiceAudio,
		curElementVoiceAudio,
		setAudioPlayed,
		voiceUrl,
		setCurVoiceUrl,
		isMuted,
		floElements,
		showSubtitle,
		resetState,
		setElementVoicePlaying,
		hasNotPlayed,
		imageElement,
		transcribeSegments,
		selectedLanguageIndex,
		ignoreLanguageAudioOverlap,
		languageAudioRef,
	]);

	const onReplayClick = useCallback(() => {
		if (curElementVoiceAudio) {
			curElementVoiceAudio.play();
			showSubtitle();
		}
		return () => {
			if (curElementVoiceAudio) {
				curElementVoiceAudio.pause();
			}
		};
	}, [curElementVoiceAudio, showSubtitle]);

	return {
		onElementVoiceMuteClick,
		showElementVoiceIcon: !!curElementVoiceAudio,
		subTitle: get(subtitlesList, `[${subtitleIndex}].text`, ''),
		isElementVoicePlaying,
		onReplayClick,
		elementVoiceTooltipTitle: isElementVoicePlaying
			? isMuted
				? 'Unmute'
				: 'Mute'
			: 'Replay element voice',
		voiceOverDuration: get(voiceProperties, 'durationInMillis', 0),
	};
};

export const useBackgroundMusic = ({
	url,
	isMuted,
	pauseOnInteractions,
	fadeInOut,
	pauseTimeList,
	currentTime,
	videoEnded,
	volume,
	duration,
	interactionsList,
	isSwiftPlay,
	isNarrationPlaying,
	hasAudioForCurrentStep,
}: {
	url: string;
	isMuted?: boolean;
	pauseOnInteractions: boolean;
	fadeInOut: boolean;
	currentTime: number;
	pauseTimeList: pauseTimeType[];
	videoEnded: boolean;
	volume: number;
	duration: number;
	interactionsList: pauseTimeType[];
	isSwiftPlay?: boolean;
	isNarrationPlaying?: boolean;
	hasAudioForCurrentStep?: boolean;
}) => {
	const [curBgAudio, setCurBgAudio] = useState<HTMLAudioElement | null>(null);
	const [curBgUrl, setCurBgUrl] = useState('');
	const [audioPlayed, setAudioPlayed] = useState(false);
	const [playFakeTimeoutStartTime, setPlayFakeTimeoutStartTime] = useState(0);
	const [animateAudio, setAnimateAudio] = useState('');
	const [volumeRange, setVolumeRange] = useState({ range: [0.5, 1], step: 0.1 });
	const [hideVoiceIcon, setHideVoiceIcon] = useState(false);
	const [isManualPause, setIsManualPause] = useState(false);
	const [wasPlayingBeforeNarration, setWasPlayingBeforeNarration] = useState(false);
	const resumeAfterVisibilityRef = useRef(false);

	const playBgAudio = useCallback((audio: HTMLAudioElement) => {
		if (typeof document !== 'undefined' && document.hidden) {
			resumeAfterVisibilityRef.current = true;
			audio.pause();
			return;
		}
		audio.play().catch(() => {});
	}, []);

	useEffect(() => {
		const minVolume = volume * BGM_MIN_VOLUME_MULTIPLIER;
		const step = (volume - minVolume) / 5;
		setVolumeRange({ range: [minVolume, volume], step });
	}, [volume]);

	const canPlay =
		currentTime !== 0 ||
		(!pauseOnInteractions && get(interactionsList, '[0].start') === 0);

	// useEffect(() => {
	// 	if (curBgAudio && canPlay) {
	// 		curBgAudio
	// 			?.play()
	// 			.then(() => {
	// 				setAudioPlayed(true);
	// 			})
	// 			.catch((reason) => {
	// 				if (reason?.name === 'NotAllowedError' && (curBgAudio.muted || !audioPlayed)) {
	// 					curBgAudio.onloadedmetadata = () => {
	// 						setPlayFakeTimeoutStartTime(new Date().getTime());
	// 					};
	// 				}
	// 			});
	// 	}
	// 	return () => {
	// 		if (curBgAudio) {
	// 			curBgAudio?.pause();
	// 		}
	// 	};
	// }, [curBgAudio, setAudioPlayed, setPlayFakeTimeoutStartTime, audioPlayed, canPlay]);

	useEffect(() => {
		if (curBgAudio) {
			curBgAudio.muted = isMuted ?? false;
		}
	}, [isMuted, curBgAudio]);

	useEffect(() => {
		if (!curBgAudio || typeof document === 'undefined') return;

		const handleVisibilityChange = () => {
			if (document.hidden) {
				if (!curBgAudio.paused) {
					resumeAfterVisibilityRef.current = true;
					curBgAudio.pause();
				}
				return;
			}

			if (resumeAfterVisibilityRef.current) {
				resumeAfterVisibilityRef.current = false;
				playBgAudio(curBgAudio);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		handleVisibilityChange();
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [curBgAudio, playBgAudio]);

	useEffect(() => {
		let interval: ReturnType<typeof setInterval>;
		if (curBgAudio && animateAudio) {
			const cb = () => {
				if (animateAudio === 'fadeIn') {
					if (curBgAudio.volume >= volumeRange.range[1]) {
						setAnimateAudio('');
						if (interval) clearInterval(interval);
						return;
					}

					curBgAudio.volume = min([
						Math.round((curBgAudio.volume + volumeRange.step) * 100) / 100,
						volumeRange.range[1],
					]) as number;
				} else {
					if (curBgAudio.volume <= volumeRange.range[0]) {
						setAnimateAudio('');
						if (interval) clearInterval(interval);
						return;
					}
					curBgAudio.volume = max([
						Math.round((curBgAudio.volume - volumeRange.step) * 100) / 100,
						volumeRange.range[0],
					]) as number;
				}
			};
			cb();
			interval = setInterval(cb, 90);
		}
		return () => {
			if (curBgAudio && animateAudio) {
				clearInterval(interval);
			}
		};
	}, [curBgAudio, animateAudio, setAnimateAudio, volumeRange]);

	// Pause & release the bg audio when the hook unmounts or the element is
	// replaced - a detached HTMLAudioElement keeps playing otherwise (e.g. when
	// the access-lock screen unmounts the player).
	useEffect(() => {
		return () => {
			if (curBgAudio) {
				curBgAudio.pause();
				curBgAudio.src = '';
			}
		};
	}, [curBgAudio]);

	const audioFadeInOut = useCallback(
		(fadeIn?: boolean) => {
			if (curBgAudio) {
				if (fadeInOut) {
					// fadeInOut is enabled: use smooth animation
					setAnimateAudio(fadeIn ? 'fadeIn' : 'fadeOut');
				}
				// If fadeInOut is false, don't change volume at all
				// BGM continues at normal 100% volume
			}
		},
		[curBgAudio, fadeInOut, setAnimateAudio]
	);

	useEffect(() => {
		if (curBgAudio && !isManualPause && !isNarrationPlaying) {
			const elementPresent = find(
				pauseTimeList,
				(item) => currentTime >= item.start - 0.5 && currentTime <= item.end + 0.5
			);
			const interactionPresent = find(
				interactionsList,
				(item) =>
					item.isInteraction &&
					item.start >= currentTime - 0.5 &&
					item.start <= currentTime + 0.5
			);

			if (pauseOnInteractions) {
				if (interactionPresent) {
					//@ts-ignore
					if (interactionPresent?.start !== 0) {
						if (fadeInOut) {
							audioFadeInOut();
							setTimeout(() => {
								curBgAudio.pause();
							}, 450);
						} else {
							curBgAudio.pause();
						}
					}
					setHideVoiceIcon(true);
				} else if (curBgAudio.paused && currentTime > 0) {
					setHideVoiceIcon(false);
					playBgAudio(curBgAudio);
					if (fadeInOut) audioFadeInOut(true);
				}
				if (elementPresent) {
					if (!interactionPresent) setHideVoiceIcon(false);
					if (fadeInOut) audioFadeInOut();
				} else if (curBgAudio.volume <= volumeRange.range[0]) {
					audioFadeInOut(true);
				}
			} else {
				if (elementPresent || interactionPresent) {
					audioFadeInOut();
				} else if (curBgAudio.volume <= volumeRange.range[0]) {
					audioFadeInOut(true);
				}
			}
		}
	}, [
		pauseOnInteractions,
		curBgAudio,
		currentTime,
		pauseTimeList,
		interactionsList,
		volumeRange,
		isManualPause,
		setHideVoiceIcon,
		fadeInOut,
		isNarrationPlaying,
		playBgAudio,
	]);

	const onAudioEnded = useCallback(() => {
		if (curBgAudio && !isManualPause) {
			playBgAudio(curBgAudio);
		}
	}, [isManualPause, curBgAudio, playBgAudio]);

	useEffect(() => {
		if (curBgAudio) {
			curBgAudio.addEventListener('ended', onAudioEnded);
		}
		return () => {
			curBgAudio?.removeEventListener('ended', onAudioEnded);
		};
	}, [curBgAudio, onAudioEnded]);
	useEffect(() => {
		if (!curBgAudio) return;

		// Only reduce volume if narration is playing AND it has actual audio
		const shouldReduceVolume = isNarrationPlaying && hasAudioForCurrentStep;

		if (shouldReduceVolume) {
			const wasAlreadyPlaying = !curBgAudio.paused;

			// Only track the initial state when narration first starts
			// (wasPlayingBeforeNarration will be false initially)
			if (wasPlayingBeforeNarration === false) {
				setWasPlayingBeforeNarration(wasAlreadyPlaying);
			}

			// Only start BGM if it was already playing before narration started
			if (wasAlreadyPlaying && curBgAudio.paused) {
				playBgAudio(curBgAudio);
			}

			// Reduce volume when narration is playing with audio (only if fadeInOut is enabled)
			if (fadeInOut) {
				// Fade to minimum volume (10%)
				audioFadeInOut(false);
			}
			// If fadeInOut is false, keep BGM at full volume
		} else {
			// Narration ended or has no audio - restore BGM state
			if (wasPlayingBeforeNarration && !isManualPause && fadeInOut) {
				// BGM was playing before narration - restore to full volume
				audioFadeInOut(true); // Fade in to maximum volume
			}
			// Reset the tracking state for next narration
			setWasPlayingBeforeNarration(false);
		}
	}, [
		isNarrationPlaying,
		hasAudioForCurrentStep,
		curBgAudio,
		fadeInOut,
		audioFadeInOut,
		wasPlayingBeforeNarration,
		isManualPause,
		volumeRange,
		playBgAudio,
	]);

	useEffect(() => {
		if (url && url !== curBgUrl && (!isSwiftPlay || !pauseOnInteractions)) {
			// Stop and release the previous background audio element before
			// replacing it. Without this, a playing <audio> element is kept
			// alive by the browser even after React drops the reference,
			// leaving it decoding/playing in the background indefinitely and
			// stacking up with every subsequent URL change.
			if (curBgAudio) {
				curBgAudio.pause();
				curBgAudio.src = '';
				curBgAudio.load();
			}
			const newAudio = new Audio(url);
			newAudio.muted = isMuted ?? false;
			// When fade in/out is enabled, start quiet so the fade-in effect has
			// somewhere to animate from. Otherwise start at the full configured
			// volume right away, since nothing else will ever raise it.
			newAudio.volume = fadeInOut ? volume * BGM_MIN_VOLUME_MULTIPLIER : volume;
			setCurBgUrl(url);
			setCurBgAudio(newAudio);
		}
	}, [
		url,
		setCurBgAudio,
		isMuted,
		curBgUrl,
		setCurBgUrl,
		volume,
		isSwiftPlay,
		pauseOnInteractions,
		fadeInOut,
	]);

	const onToggleMuteBgCb = useCallback(() => {
		if (curBgAudio) {
			const willUnmute = isMuted; // Current state is muted, so toggle means unmute
			curBgAudio.muted = !isMuted;

			if (playFakeTimeoutStartTime) {
				const timeElapsed = new Date().getTime() - playFakeTimeoutStartTime;
				const duration = timeElapsed % (curBgAudio.duration * 1000);
				curBgAudio.currentTime = duration / 1000;
				playBgAudio(curBgAudio);
				setPlayFakeTimeoutStartTime(0);
			} else if (curBgAudio.paused) {
				playBgAudio(curBgAudio);
			}

			// If unmuting while narration is playing with audio, set appropriate volume
			const shouldReduceVolume = isNarrationPlaying && hasAudioForCurrentStep;
			if (willUnmute && shouldReduceVolume) {
				if (fadeInOut) {
					// Set to 10% volume since narration is active with audio
					curBgAudio.volume = volumeRange.range[0];
				} else {
					// Set to full 100% volume
					curBgAudio.volume = volumeRange.range[1];
				}
			}
		}
	}, [
		curBgAudio,
		isMuted,
		playFakeTimeoutStartTime,
		isNarrationPlaying,
		hasAudioForCurrentStep,
		fadeInOut,
		volumeRange,
		playBgAudio,
	]);

	const onRestartBgCb = useCallback(() => {
		if (curBgAudio) {
			curBgAudio.currentTime = 0;
			if (!pauseOnInteractions && curBgAudio.paused) {
				playBgAudio(curBgAudio);
			}
		}
	}, [curBgAudio, pauseOnInteractions, playBgAudio]);

	useEffect(() => {
		if (videoEnded) {
			resumeAfterVisibilityRef.current = false;
			setIsManualPause(true);
			if (fadeInOut) {
				audioFadeInOut();
				setTimeout(() => {
					curBgAudio?.pause();
				}, 450);
			} else {
				curBgAudio?.pause();
			}
		}
	}, [videoEnded, curBgAudio, fadeInOut, setIsManualPause]);

	useEffect(() => {
		if (isManualPause && currentTime === 0) {
			const anyInteractionAtStart = find(
				interactionsList,
				(item) => item.isInteraction && item.start === 0
			);
			if (!anyInteractionAtStart && !curBgAudio?.paused) {
				curBgAudio?.pause();
			}
		}
	}, [curBgAudio, isManualPause, currentTime, interactionsList]);

	useEffect(() => {
		if (curBgAudio && curBgAudio.volume > volumeRange.range[1]) {
			if (fadeInOut) {
				curBgAudio.volume = volumeRange.range[0];
			} else {
				curBgAudio.volume = volumeRange.range[1];
			}
		}
	}, [volumeRange, curBgAudio, fadeInOut]);

	const onVideoPauseBgCb = useCallback(() => {
		if (curBgAudio) {
			resumeAfterVisibilityRef.current = false;
			setIsManualPause(true);
			curBgAudio.pause();
		}
	}, [curBgAudio, setIsManualPause]);

	const onPlayBgCb = useCallback(
		(forceMuted?: boolean) => {
			if (curBgAudio) {
				setIsManualPause(false);
				if (!isNarrationPlaying) {
					curBgAudio.muted = forceMuted ?? isMuted ?? false;
					playBgAudio(curBgAudio);
				}
			}
		},
		[curBgAudio, setIsManualPause, isNarrationPlaying, isMuted, playBgAudio]
	);

	const onVideoEndBgCb = useCallback(() => {
		if (curBgAudio) {
			resumeAfterVisibilityRef.current = false;
			curBgAudio.currentTime = 0;
			curBgAudio.pause();
			setIsManualPause(true);
		}
	}, [curBgAudio, setIsManualPause]);

	return {
		onToggleMuteBgCb,
		onRestartBgCb,
		onVideoPauseBgCb,
		onPlayBgCb,
		hideVoiceIcon,
		onVideoEndBgCb,
	};
};

export const getElementVoiceResources = (
	currentElement: publishElementsDataType | undefined | null,
	elementVoiceResources:
		| { [key: string]: { voiceId: string; scriptId: string } }
		| undefined,
	floElements: {
		[key: string]: publishElementsDataType[];
	}
) => {
	let element = currentElement;
	let imageElement;
	if (!get(currentElement, 'enableVoice') && get(currentElement, 'linkedImageId')) {
		element = find(get(floElements, 'linked_images'), [
			'id',
			get(currentElement, 'linkedImageId'),
		]);
		imageElement = element;
		//@ts-ignore
	}
	return {
		voiceProperties: get(
			elementVoiceResources,
			`${get(element, 'voiceConfig.scriptId')}_${get(element, 'voiceConfig.voiceId')}`
		),
		imageElement,
	};
};

export const preventDefaultForTouchEndEvent = (
	event?: React.MouseEvent | React.TouchEvent
) => {
	if (event?.type === 'touchend') {
		event.preventDefault();
	}
};

export const getResourceHeadersForDoc360 = (floIndex: number, token: string) => {
	const embeddedElements = document.querySelectorAll('floik-flo');
	const region = embeddedElements[floIndex].getAttribute('doc360-region');
	const deploymentId =
		embeddedElements[floIndex].getAttribute('doc360-deployment-id') || '';
	return {
		Authorization: `Bearer ${token}`,
		'x-d360-id': deploymentId,
		'x-d360-region': region,
	};
};

export async function getMediaDimensionsFromUrl(
	mediaUrl: string
): Promise<{ width: number; height: number } | null> {
	return new Promise((resolve, reject) => {
		const isVideo = mediaUrl.includes('.mp4');

		if (isVideo) {
			const video = document.createElement('video');
			video.onloadedmetadata = () => {
				resolve({ width: video.videoWidth, height: video.videoHeight });
			};
			video.onerror = () => reject(null);
			video.src = mediaUrl;
		} else {
			const img = new Image();
			img.onload = () => {
				resolve({ width: img.width, height: img.height });
			};
			img.onerror = () => reject(null);
			img.src = mediaUrl;
		}
	});
}

export const getThemeDetails = (theme: string) => {
	// Handle custom theme format: 'custom_COLOR' or 'custom_#COLOR'
	if (theme?.startsWith('custom_')) {
		const color = theme.substring(7); // Extract color after 'custom_'
		return color.startsWith('#') ? color : `#${color}`;
	}
	if (theme?.startsWith('brand_')) {
		const color = theme.substring(6); // Extract color after 'custom_'
		return color.startsWith('#') ? color : `#${color}`;
	}

	const supportedThemes = [
		//added default theme color here
		{
			key: 'none',
			label: 'No Theme',
			color: 'none',
		},
		{
			key: 'peach',
			label: 'Peach',
			color: '#F7C7B3',
		},
		{
			key: 'light_blue',
			label: 'Light Blue',
			color: '#CBD8FF',
		},
		{
			key: 'mint',
			label: 'Mint',
			color: '#99D7A8',
		},
		{
			key: 'navy',
			label: 'Navy',
			color: '#2D3C67',
		},
		{
			key: 'plum',
			label: 'Plum',
			color: '#522E47',
		},
		{
			key: 'dark_gray',
			label: 'Dark Gray',
			color: '#374E4E',
		},
		{
			key: 'teal',
			label: 'Teal',
			color: '#155E75',
		},
		{
			key: 'gradient_mint',
			label: 'Gradient Mint',
			gradient: 'linear-gradient(180deg, #13547A, #80D0C7)',
		},
		{
			key: 'gradient_blue',
			label: 'Gradient Blue',
			gradient: 'linear-gradient(180deg, #0F469D, #3189ED)',
		},
		{
			key: 'gradient_purple',
			label: 'Gradient Purple',
			gradient: 'linear-gradient(180deg, #663177, #AF7EA4)',
		},
		{
			key: 'gradient_indigo',
			label: 'Gradient Indigo',
			gradient: 'linear-gradient(90deg, #1945AE, #60399D)',
		},
	];
	const themeDetails = supportedThemes.find((t) => t.key === theme);

	return themeDetails ? themeDetails.color || themeDetails.gradient : null;
};

export const getAnimationVariant = (theme: string) => {
	const supportedThemes = [
		{
			key: 'purple',
			label: 'Purple',
			color: '#5227FF',
			variant: 0,
		},
		{
			key: 'wine',
			label: 'Wine',
			color: '#6e1c1c',
			variant: 1,
		},
		{
			key: 'forest',
			label: 'Forest',
			color: '#2a9a70',
			variant: 2,
		},
		{
			key: 'sunset',
			label: 'Sunset',
			color: '#ef6c0f',
			variant: 3,
		},
		{
			key: 'silver',
			label: 'Silver',
			color: '#808080',
			variant: 4,
		},
	];
	const variant = supportedThemes.find((t) => t.key === theme);

	return variant ? variant.variant : null;
};

export const getContrastColor = (hex: string) => {
	// Handle linear gradient values
	if (hex?.includes('linear-gradient')) {
		// Extract the first color from the gradient
		const match = hex.match(/#[0-9A-Fa-f]{6}/);
		if (match) {
			hex = match[0];
		} else {
			return '#000'; // Default to black if no valid hex color found in gradient
		}
	}

	// Convert HEX to RGB
	const rgb = hexToRgb(hex);
	if (!rgb) return '#000'; // Default to black if invalid color

	// Calculate luminance
	const luminance =
		(0.2126 * rgb.r) / 255 + (0.7152 * rgb.g) / 255 + (0.0722 * rgb.b) / 255;

	// Return black (#000) for light colors, white (#fff) for dark colors
	return luminance > 0.5 ? '#222' : '#fff';
};

// Helper: Convert HEX to RGB
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
	hex = hex.replace(/^#/, ''); // Remove #
	if (hex.length === 3) {
		hex = hex
			.split('')
			.map((c) => c + c)
			.join(''); // Convert #RGB to #RRGGBB
	}
	const match = hex.match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
	return match
		? {
				r: parseInt(match[1], 16),
				g: parseInt(match[2], 16),
				b: parseInt(match[3], 16),
		  }
		: null;
}

export const getColorWithOpacity = (hex: string, opacity: number): string => {
	if (!hex) return '';

	hex = hex.replace('#', '').trim();
	if (hex.length === 3) {
		hex = hex
			.split('')
			.map((c) => c + c)
			.join('');
	}

	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

type WrapperType = 'rounded' | 'light' | 'dark';

export function getWrapperValue(
	wrapperValue: string | string[] | ParsedQs | ParsedQs[] | null
): WrapperType {
	// Handle null or undefined
	if (wrapperValue === null || wrapperValue === undefined) {
		return 'rounded';
	}

	// Convert to string if it's an array or ParsedQs
	const value =
		typeof wrapperValue === 'string'
			? wrapperValue
			: Array.isArray(wrapperValue)
			? String(wrapperValue[0] || '')
			: String(wrapperValue);

	// Check valid values
	switch (value) {
		case 'light':
		case 'dark':
			return value as WrapperType;
		default:
			return 'rounded';
	}
}

export const transformGuideElements = (elements: elementBeanListType[], steps) => {
	const clickElementsFromSteps = reduce(
		steps,
		(acc, step) => {
			return {
				...acc,
				[step.elementId]: [step.stepId],
			};
		},
		{}
	);
	const stepCrops = reduce(
		elements,
		(
			acc: { [key: string]: { [key: string]: elementBeanListType } },
			item: elementBeanListType
		) => {
			if (item?.editorElementName === 'crop') {
				forEach(item.stepIds, (stepId) => {
					acc = set(acc, `${stepId}`, item);
				});
			}
			return acc;
		},
		{}
	);

	return reduce(
		elements,
		(
			acc: { [key: string]: { [key: string]: elementBeanListType[] } },
			item: elementBeanListType
		) => {
			const elementGroupName =
				item?.editorElementName === 'document-blur' ? 'blur' : item.editorElementName;
			let newItem = {
				...item,
				stepIds: get(item, 'stepIds') || get(clickElementsFromSteps, item.id) || [],
			};

			forEach(newItem.stepIds, (stepId) => {
				const newValue = get(acc, `${stepId}.${elementGroupName}`, []) || [];

				const cropData = get(stepCrops, stepId);
				let isCropped = !!cropData;
				// lodash's `get()` can't infer a leaf type for a path this deep, so
				// it falls back to a union with cropData's own type (e.g.
				// `0 | elementBeanListType`), which d3's scaleLinear (expecting a
				// plain NumberValue) rejects below. These are always numeric
				// canvas coordinates, so cast explicitly.
				const actualCropX = get(
					cropData,
					'properties.canvasRenderConfig.canvasCoordinates.x',
					0
				) as number;
				const actualCropY = get(
					cropData,
					'properties.canvasRenderConfig.canvasCoordinates.y',
					0
				) as number;

				const actualCropCanvasWidth = get(
					cropData,
					'properties.canvasRenderConfig.canvasCoordinates.width',
					0
				) as number;
				const actualCropCanvasHeight = get(
					cropData,
					'properties.canvasRenderConfig.canvasCoordinates.height',
					0
				) as number;
				const actualCropWindowWidth = get(
					cropData,
					'properties.canvasRenderConfig.windowCoordinates.width',
					0
				) as number;
				const actualCropWindowHeight = get(
					cropData,
					'properties.canvasRenderConfig.windowCoordinates.height',
					0
				) as number;

				const videoResourceProperties = {
					width: actualCropCanvasWidth,
					height: actualCropCanvasHeight,
				};

				switch (get(item, 'editorElementName')) {
					case 'text': {
						const canvasRenderConfig = get(item, 'properties.canvasRenderConfig');
						const clickWindowWidth = get(
							canvasRenderConfig,
							'windowCoordinates.width',
							0
						);
						const clickWindowHeight = get(
							canvasRenderConfig,
							'windowCoordinates.height',
							0
						);
						const scaleCropToTextX = scaleLinear(
							[0, actualCropWindowWidth],
							[0, get(canvasRenderConfig, 'windowCoordinates.width', 0)]
						);
						const scaleCropToTextY = scaleLinear(
							[0, actualCropWindowHeight],
							[0, get(canvasRenderConfig, 'windowCoordinates.height', 0)]
						);
						const cropX = scaleCropToTextX(actualCropX);
						const cropY = scaleCropToTextY(actualCropY);
						const cropCanvasWidth = scaleCropToTextX(actualCropCanvasWidth);
						const cropCanvasHeight = scaleCropToTextY(actualCropCanvasHeight);

						const clickWindowScaleX = scaleLinear(
							[cropX, cropX + cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleY = scaleLinear(
							[cropY, cropY + cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const clickWindowScaleWidth = scaleLinear(
							[0, cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleHeight = scaleLinear(
							[0, cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const pointerScaleX = scaleLinear(
							[0, clickWindowWidth],
							[0, videoResourceProperties.width]
						);
						const pointerScaleY = scaleLinear(
							[0, clickWindowHeight],
							[0, videoResourceProperties.height]
						);

						const clickPositionX = pointerScaleX(
							(isCropped ? clickWindowScaleX : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.x', 0)
							)
						);
						const clickPositionY = pointerScaleY(
							(isCropped ? clickWindowScaleY : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.y', 0)
							)
						);
						const newClickWidth = pointerScaleX(
							(isCropped ? clickWindowScaleWidth : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.width', 0)
							)
						);
						const newClickHeight = pointerScaleY(
							(isCropped ? clickWindowScaleHeight : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.height', 0)
							)
						);

						const fontSize = get(item, 'properties.fontSize', 8);
						const scaledFontSize = get(item, 'properties.scaledFontSize', 8);

						// Use the average of the scaling factors to maintain proportionality
						const averageScale = (clickWindowWidth / 600 + clickWindowHeight / 600) / 2;
						// Calculate the font size
						let newFontSize = fontSize * averageScale;

						let fontCropData = {};

						if (isCropped) {
							newFontSize = scaledFontSize;
							fontCropData = {
								fontCropWidth: cropCanvasWidth,
								fontCropHeight: cropCanvasHeight,
								fontEleWidth: get(canvasRenderConfig, 'windowCoordinates.width', 0),
								fontEleHeight: get(canvasRenderConfig, 'windowCoordinates.width', 0),
							};
						}

						const width = Math.abs(newClickWidth);
						const height = Math.abs(newClickHeight);

						const newCanvasRenderConfig = {
							canvasCoordinates: {
								x: clickPositionX,
								y: clickPositionY,
								width,
								height,
								angle: get(canvasRenderConfig, 'canvasCoordinates.angle', 0),
							},
							windowCoordinates: {
								width: videoResourceProperties.width,
								height: videoResourceProperties.height,
							},
						};

						newItem = {
							...newItem,
							fontSize: newFontSize,
							cropped: isCropped,
							fontCropData: fontCropData,
							canvasRenderConfig: isCropped ? newCanvasRenderConfig : canvasRenderConfig,
						};
						break;
					}
					case 'highlight':
					case 'document-spotlight':
					case 'spotlight':
					case 'blur':
					case 'document-blur': {
						const canvasRenderConfig = get(item, 'properties.canvasRenderConfig');

						const clickWindowWidth = get(
							canvasRenderConfig,
							'windowCoordinates.width',
							0
						);
						const clickWindowHeight = get(
							canvasRenderConfig,
							'windowCoordinates.height',
							0
						);

						const scaleCropToBlurX = scaleLinear(
							[0, actualCropWindowWidth],
							[0, get(canvasRenderConfig, 'windowCoordinates.width', 0)]
						);
						const scaleCropToBlurY = scaleLinear(
							[0, actualCropWindowHeight],
							[0, get(canvasRenderConfig, 'windowCoordinates.height', 0)]
						);
						const cropX = scaleCropToBlurX(actualCropX);
						const cropY = scaleCropToBlurY(actualCropY);
						const cropCanvasWidth = scaleCropToBlurX(actualCropCanvasWidth);
						const cropCanvasHeight = scaleCropToBlurY(actualCropCanvasHeight);

						console.log([cropX, cropX + cropCanvasWidth], { clickWindowWidth });
						const clickWindowScaleX = scaleLinear(
							[cropX, cropX + cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleY = scaleLinear(
							[cropY, cropY + cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const clickWindowScaleWidth = scaleLinear(
							[0, cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleHeight = scaleLinear(
							[0, cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const pointerScaleX = scaleLinear(
							[0, clickWindowWidth],
							[0, videoResourceProperties.width]
						);
						const pointerScaleY = scaleLinear(
							[0, clickWindowHeight],
							[0, videoResourceProperties.height]
						);

						const clickPositionX = pointerScaleX(
							(isCropped ? clickWindowScaleX : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.x', 0)
							)
						);
						const clickPositionY = pointerScaleY(
							(isCropped ? clickWindowScaleY : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.y', 0)
							)
						);
						const newClickWidth = pointerScaleX(
							(isCropped ? clickWindowScaleWidth : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.width', 0)
							)
						);
						const newClickHeight = pointerScaleY(
							(isCropped ? clickWindowScaleHeight : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.height', 0)
							)
						);

						const width = Math.abs(newClickWidth);
						const height = Math.abs(newClickHeight);

						const newCanvasRenderConfig = {
							canvasCoordinates: {
								x: clickPositionX,
								y: clickPositionY,
								width,
								height,
							},
							windowCoordinates: {
								width: videoResourceProperties.width,
								height: videoResourceProperties.height,
							},
						};
						newItem.canvasRenderConfig = isCropped
							? newCanvasRenderConfig
							: canvasRenderConfig;
						break;
					}
					case 'image': {
						const canvasRenderConfig = get(item, 'properties.canvasRenderConfig');
						const fitToScreen = get(item, 'properties.fitToScreen');
						const clickWindowWidth = get(
							canvasRenderConfig,
							'windowCoordinates.width',
							0
						);
						const clickWindowHeight = get(
							canvasRenderConfig,
							'windowCoordinates.height',
							0
						);

						const scaleCropToBlurX = scaleLinear(
							[0, actualCropWindowWidth],
							[0, get(canvasRenderConfig, 'windowCoordinates.width', 0)]
						);
						const scaleCropToBlurY = scaleLinear(
							[0, actualCropWindowHeight],
							[0, get(canvasRenderConfig, 'windowCoordinates.height', 0)]
						);
						const cropX = scaleCropToBlurX(actualCropX);
						const cropY = scaleCropToBlurY(actualCropY);
						const cropCanvasWidth = scaleCropToBlurX(actualCropCanvasWidth);
						const cropCanvasHeight = scaleCropToBlurY(actualCropCanvasHeight);

						const clickWindowScaleX = scaleLinear(
							[cropX, cropX + cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleY = scaleLinear(
							[cropY, cropY + cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const clickWindowScaleWidth = scaleLinear(
							[0, cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleHeight = scaleLinear(
							[0, cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const pointerScaleX = scaleLinear(
							[0, clickWindowWidth],
							[0, videoResourceProperties.width]
						);
						const pointerScaleY = scaleLinear(
							[0, clickWindowHeight],
							[0, videoResourceProperties.height]
						);

						const clickPositionX = pointerScaleX(
							(isCropped ? clickWindowScaleX : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.x', 0)
							)
						);
						const clickPositionY = pointerScaleY(
							(isCropped ? clickWindowScaleY : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.y', 0)
							)
						);
						const newClickWidth = pointerScaleX(
							(isCropped ? clickWindowScaleWidth : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.width', 0)
							)
						);
						const newClickHeight = pointerScaleY(
							(isCropped ? clickWindowScaleHeight : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.height', 0)
							)
						);

						const width = Math.abs(newClickWidth);
						const height = Math.abs(newClickHeight);

						const newCanvasRenderConfig = {
							canvasCoordinates: {
								x: clickPositionX,
								y: clickPositionY,
								width,
								height,
								angle: get(canvasRenderConfig, 'canvasCoordinates.angle', 0),
							},
							windowCoordinates: {
								width: videoResourceProperties.width,
								height: videoResourceProperties.height,
							},
						};
						newItem.canvasRenderConfig = isCropped
							? newCanvasRenderConfig
							: canvasRenderConfig;
						break;
					}
					case 'hotspots':
					case 'added-click':
					case 'clicks': {
						const canvasRenderConfig = get(item, 'properties.canvasRenderConfig');
						const clickWindowWidth = get(
							canvasRenderConfig,
							'windowCoordinates.width',
							0
						);
						const clickWindowHeight = get(
							canvasRenderConfig,
							'windowCoordinates.height',
							0
						);
						const scaleCropToPointerX = scaleLinear(
							[
								0,
								get(cropData, 'properties.canvasRenderConfig.windowCoordinates.width', 0),
							],
							[0, get(canvasRenderConfig, 'windowCoordinates.width', 0)]
						);
						const scaleCropToPointerY = scaleLinear(
							[
								0,
								get(
									cropData,
									'properties.canvasRenderConfig.windowCoordinates.height',
									0
								),
							],
							[0, get(canvasRenderConfig, 'windowCoordinates.height', 0)]
						);
						const cropX = scaleCropToPointerX(actualCropX);
						const cropY = scaleCropToPointerY(actualCropY);
						const cropCanvasWidth = scaleCropToPointerX(actualCropCanvasWidth);
						const cropCanvasHeight = scaleCropToPointerY(actualCropCanvasHeight);

						const clickWindowScaleX = scaleLinear(
							[cropX, cropX + cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleY = scaleLinear(
							[cropY, cropY + cropCanvasHeight],
							[0, clickWindowHeight]
						);
						const clickWindowScaleWidth = scaleLinear(
							[cropX, cropX + cropCanvasWidth],
							[0, clickWindowWidth]
						);

						const clickWindowScaleHeight = scaleLinear(
							[cropY, cropY + cropCanvasHeight],
							[0, clickWindowHeight]
						);

						const pointerScaleX = scaleLinear(
							[0, clickWindowWidth],
							[0, videoResourceProperties.width]
						);
						const pointerScaleY = scaleLinear(
							[0, clickWindowHeight],
							[0, videoResourceProperties.height]
						);

						const clickPositionX = pointerScaleX(
							(isCropped ? clickWindowScaleX : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.x', 0)
							)
						);
						const clickPositionY = pointerScaleY(
							(isCropped ? clickWindowScaleY : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.y', 0)
							)
						);
						const newClickWidth = pointerScaleX(
							(isCropped ? clickWindowScaleWidth : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.width', 0)
							)
						);
						const newClickHeight = pointerScaleX(
							(isCropped ? clickWindowScaleHeight : (i: number): number => i)(
								get(canvasRenderConfig, 'canvasCoordinates.height', 0)
							)
						);

						const newClickPositionX = clickPositionX;
						const newClickPositionY = clickPositionY;

						const cx = Math.abs(newClickPositionX);
						const cy = Math.abs(newClickPositionY);

						const width = Math.abs(newClickWidth);
						const height = Math.abs(newClickHeight);

						const newCanvasRenderConfig = {
							canvasCoordinates: {
								x: cx,
								y: cy,
								width,
								height,
							},
							windowCoordinates: {
								width: videoResourceProperties.width,
								height: videoResourceProperties.height,
							},
						};
						newItem.canvasRenderConfig = isCropped
							? newCanvasRenderConfig
							: canvasRenderConfig;
						break;
					}
				}
				//@ts-ignore
				newValue.push(newItem);
				acc = set(acc, `${stepId}.${elementGroupName}`, newValue);
			});
			return acc;
		},
		{}
	);
};
